// SignupPage — shown after a successful phone OTP when the user has no
// completed players row (tos_accepted_at IS NULL). Collects the 11 signup
// fields and POSTs to /.netlify/functions/complete-signup.
//
// On success, AuthContext stores the player row and the auth gate flips
// the user into the app.

import { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useTheme } from '../contexts/ThemeContext'
import { useShellStatusBar } from '../lib/statusBar'
// F-094-T1: shared age helper. Signup floor is 13 (D-352, ToS §1 alignment).
// UTC-anchored parse — mirrors the netlify/functions/_ageGate.js convention
// so client + server agree at day boundaries.
import { isUnderSignupAge, SIGNUP_MIN_AGE } from '../lib/ageGate'

const GENDERS = [
  { v: 'male', label: 'Male' },
  { v: 'female', label: 'Female' },
  { v: 'nonbinary', label: 'Non-binary' },
  { v: 'prefer_not_to_say', label: 'Prefer not to say' },
]

export default function SignupPage() {
  const { completeSignup, signOutAccount } = useAuth()
  const { theme } = useTheme()

  // Shell screen with a fixed dark Matte Navy background — force LIGHT (Android)
  // status-bar icons while mounted, independent of the golf theme.
  useShellStatusBar(theme)

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [displayNameDirty, setDisplayNameDirty] = useState(false)
  const [email, setEmail] = useState('')
  const [birthdate, setBirthdate] = useState('')
  const [gender, setGender] = useState('')
  const [starterIndex, setStarterIndex] = useState('')
  const [ghin, setGhin] = useState('')
  const [tos, setTos] = useState(false)
  const [marketing, setMarketing] = useState(false)
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  // Default display_name to first + last unless user has typed in it
  const onFirst = (v) => {
    setFirstName(v)
    if (!displayNameDirty) setDisplayName(`${v} ${lastName}`.trim())
  }
  const onLast = (v) => {
    setLastName(v)
    if (!displayNameDirty) setDisplayName(`${firstName} ${v}`.trim())
  }
  const onDisplay = (v) => {
    setDisplayName(v)
    setDisplayNameDirty(true)
  }

  // F-094-T1: signup age floor is 13 (D-352, ToS §1 alignment). Client
  // computes from the picked birthdate so we block BEFORE the network
  // round-trip; server (complete-signup.js) enforces the same floor as
  // the ultimate boundary.
  const ageBelowMin = birthdate ? isUnderSignupAge(birthdate) : false
  const ageErrorCopy = `You must be at least ${SIGNUP_MIN_AGE} years old to create a TournyFlex account.`

  const canSubmit =
    firstName.trim() && lastName.trim() && displayName.trim() &&
    email.trim() && birthdate && !ageBelowMin && gender && tos && !submitting

  const onSubmit = async (e) => {
    e.preventDefault()
    if (!canSubmit) {
      // Surface the age reason inline even though the button is disabled —
      // a browser autofilling an under-13 birthdate should still see WHY.
      if (ageBelowMin) setError(ageErrorCopy)
      return
    }
    setError(''); setSubmitting(true)
    const result = await completeSignup({
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      display_name: displayName.trim(),
      email: email.trim(),
      birthdate,
      gender,
      starter_handicap_index: starterIndex === '' ? null : parseFloat(starterIndex),
      ghin_number: ghin.trim() || null,
      tos_accepted: true,
      marketing_opt_in: marketing,
    })
    setSubmitting(false)
    if (result.error) setError(result.error)
    // On success the auth gate (App.jsx) re-renders into the app automatically.
  }

  return (
    <div className="page" data-shell="tournyflex">
      <div className="container" style={{ paddingTop: 24, paddingBottom: 40, maxWidth: 480 }}>
        <p style={{ fontFamily: 'var(--font-mono)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.15em', color: 'var(--gold)', marginBottom: 8, textAlign: 'center' }}>
          Welcome to TournyFlex
        </p>
        <h1 style={{ marginBottom: 8, textAlign: 'center' }}>Finish your profile</h1>
        <p className="text-muted text-sm" style={{ marginBottom: 24, textAlign: 'center' }}>
          A few details and you're in.
        </p>

        <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Row label="First name" required>
            <input className="input" value={firstName} onChange={e => onFirst(e.target.value)} autoComplete="given-name" required />
          </Row>
          <Row label="Last name" required>
            <input className="input" value={lastName} onChange={e => onLast(e.target.value)} autoComplete="family-name" required />
          </Row>
          <Row label="Display name" hint="Shown on scorecards and leaderboards." required>
            <input className="input" value={displayName} onChange={e => onDisplay(e.target.value)} required />
          </Row>
          <Row label="Email" required>
            <input className="input" type="email" value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" required />
          </Row>
          <Row label="Birthdate" hint="Required. Used for age-restricted features." required>
            <input className="input" type="date" value={birthdate} onChange={e => setBirthdate(e.target.value)} required />
            {ageBelowMin && (
              <p role="alert" style={{ color: 'var(--red)', fontSize: '0.8rem', marginTop: 4 }}>
                {ageErrorCopy}
              </p>
            )}
          </Row>
          <Row label="Gender" required>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {GENDERS.map(g => (
                <button
                  type="button"
                  key={g.v}
                  className={`btn btn-sm ${gender === g.v ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => setGender(g.v)}
                >{g.label}</button>
              ))}
            </div>
          </Row>
          <Row label="Starter handicap index" hint="Optional. Your current handicap if you have one.">
            <input className="input" type="number" step="0.1" value={starterIndex} onChange={e => setStarterIndex(e.target.value)} placeholder="e.g. 12.4" />
          </Row>
          <Row label="GHIN number" hint="Optional.">
            <input className="input" value={ghin} onChange={e => setGhin(e.target.value)} />
          </Row>

          <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 8, fontSize: '0.9rem' }}>
            <input type="checkbox" checked={tos} onChange={e => setTos(e.target.checked)} style={{ marginTop: 3 }} />
            <span>
              I accept the{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--gold)', textDecoration: 'underline' }}>Terms of Service</a>
              {', '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--gold)', textDecoration: 'underline' }}>Privacy Policy</a>
              {', and '}
              <a href="https://tournyflex.com/cookies" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--gold)', textDecoration: 'underline' }}>Cookie Policy</a>.
              {' '}I understand that TournyFlex will send me SMS messages including round invitations, score attestation requests, and side-game notifications. Message and data rates may apply. Reply STOP to opt out at any time.
            </span>
          </label>
          <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: '0.9rem' }}>
            <input type="checkbox" checked={marketing} onChange={e => setMarketing(e.target.checked)} style={{ marginTop: 3 }} />
            <span>Send me occasional SMS updates about new TournyFlex features and offers (up to 2/month). Optional. Reply STOP to opt out.</span>
          </label>

          {error && <p style={{ color: 'var(--red)', fontSize: '0.85rem' }}>{error}</p>}

          <button type="submit" className="btn btn-primary btn-full" disabled={!canSubmit} style={{ marginTop: 8 }}>
            {submitting ? 'Setting up…' : 'Finish'}
          </button>
          <button type="button" className="btn btn-ghost btn-full" onClick={signOutAccount}>
            Cancel and sign out
          </button>
        </form>
      </div>
    </div>
  )
}

function Row({ label, hint, required, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: '0.85rem', fontWeight: 500 }}>
        {label}{required && <span style={{ color: 'var(--gold)' }}> *</span>}
      </label>
      {children}
      {hint && <p className="text-muted" style={{ fontSize: '0.75rem' }}>{hint}</p>}
    </div>
  )
}
